import { useState } from 'react';
import { FileText, ZoomIn, Download, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * Primary tech-spec preview for the material detail page.
 *
 * - When a PDF (spec_sheet / data_sheet / safety_data_sheet) exists for the
 *   material, an inline PDF viewer (<iframe>) is rendered with a
 *   magnifying-glass button that opens a full-screen zoom modal.
 * - When no PDF is available, a clear upload-prompt placeholder is shown so
 *   sellers know they must upload a technical data sheet.
 */
export const SpecSheetPreview = ({ specSheet, onUploadClick, canManage }) => {
  const [zoomOpen, setZoomOpen] = useState(false);

  if (!specSheet) {
    return (
      <div
        className="aspect-[4/3] rounded-lg border-2 border-dashed border-orange-300 dark:border-orange-600/50 bg-orange-50/40 dark:bg-orange-950/20 flex flex-col items-center justify-center gap-3 p-6 text-center"
        data-testid="spec-sheet-placeholder"
      >
        <div className="h-14 w-14 rounded-full bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
          <FileText className="h-7 w-7 text-orange-500" />
        </div>
        <div>
          <p className="font-semibold text-base text-foreground">
            Kein technisches Datenblatt vorhanden
          </p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">
            Käufer benötigen das technische Datenblatt zur Bewertung des Materials.
            Bitte laden Sie ein PDF hoch.
          </p>
        </div>
        {canManage && onUploadClick && (
          <Button
            variant="default"
            size="sm"
            className="bg-orange-500 hover:bg-orange-600 mt-2"
            onClick={onUploadClick}
            data-testid="spec-sheet-upload-prompt"
          >
            <Upload className="h-4 w-4 mr-1.5" />
            Datenblatt jetzt hochladen
          </Button>
        )}
      </div>
    );
  }

  const filename = specSheet.filename || specSheet.file_url?.split('/').pop() || 'datenblatt.pdf';

  return (
    <>
      <div
        className="relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 group"
        data-testid="spec-sheet-preview"
      >
        <iframe
          src={`${specSheet.file_url}#view=FitH&toolbar=0&navpanes=0`}
          title={filename}
          className="w-full h-full"
          data-testid="spec-sheet-iframe"
        />
        <div className="absolute top-3 right-3 flex gap-2 opacity-90">
          <Button
            size="icon"
            variant="secondary"
            className="h-9 w-9 shadow-md bg-white/95 hover:bg-white dark:bg-slate-800/95 dark:hover:bg-slate-800"
            onClick={() => setZoomOpen(true)}
            data-testid="spec-sheet-zoom-btn"
            aria-label="PDF vergrößern"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="secondary"
            className="h-9 w-9 shadow-md bg-white/95 hover:bg-white dark:bg-slate-800/95 dark:hover:bg-slate-800"
            asChild
            data-testid="spec-sheet-download-btn"
            aria-label="PDF herunterladen"
          >
            <a href={specSheet.file_url} target="_blank" rel="noopener noreferrer" download>
              <Download className="h-4 w-4" />
            </a>
          </Button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 py-2.5 text-white text-xs flex items-center gap-2 pointer-events-none">
          <FileText className="h-3.5 w-3.5" />
          <span className="font-medium">Technisches Datenblatt</span>
          <span className="opacity-80 truncate">· {filename}</span>
        </div>
      </div>

      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent
          className={cn(
            'max-w-[95vw] w-[95vw] h-[92vh] p-0 gap-0 overflow-hidden',
            'sm:max-w-[95vw]'
          )}
          data-testid="spec-sheet-zoom-modal"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b bg-background">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-orange-500 shrink-0" />
              <p className="font-semibold text-sm truncate">{filename}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" asChild data-testid="spec-sheet-zoom-download">
                <a href={specSheet.file_url} target="_blank" rel="noopener noreferrer" download>
                  <Download className="h-4 w-4 mr-1.5" />
                  Download
                </a>
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setZoomOpen(false)}
                data-testid="spec-sheet-zoom-close"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <iframe
            src={`${specSheet.file_url}#view=Fit&toolbar=1`}
            title={`${filename} (vergrößert)`}
            className="w-full h-full bg-slate-100 dark:bg-slate-900"
            data-testid="spec-sheet-zoom-iframe"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Returns the best PDF attachment to display as the spec-sheet preview.
 * Preference order: spec_sheet → data_sheet → safety_data_sheet → first PDF.
 */
export const pickSpecSheet = (attachments = []) => {
  const docs = attachments.filter((a) => a.type && a.type !== 'photo');
  if (docs.length === 0) return null;
  const byType = (type) => docs.find((a) => a.type === type);
  const att =
    byType('spec_sheet') ||
    byType('data_sheet') ||
    byType('safety_data_sheet') ||
    docs[0];
  if (!att) return null;
  // Rewrite any stale host (e.g. older preview URL stored in DB) to the
  // current backend so the iframe can actually load the PDF.
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  let url = att.file_url || '';
  const apiIdx = url.indexOf('/api/files/');
  if (backend && apiIdx >= 0) {
    url = `${backend}${url.slice(apiIdx)}`;
  }
  return {
    file_url: url,
    filename: att.filename || url.split('/').pop(),
    type: att.type,
    attachment_id: att.attachment_id,
  };
};
